import { PrimarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import ReactTooltip from 'react-tooltip'

import {
  selectCurrentHotelName,
  selectIsInternalAdmin,
  selectIsInvoiceGroupView
} from '../../accessors/index'
import {
  getInvoiceContacts,
  getInvoicesList,
  getPaymentsList,
  postPaymentRetry,
  updatePayment
} from '../../actions/billing'
import BillingContactsDialog from '../../components/ReusableComponents/BillingContactsDialog/index'
import ConfirmationDialog from '../../components/ReusableComponents/ConfirmationDialog/index'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import MarkAsPaid from '../../components/ReusableComponents/MarkAsPaidDialog/index'
import styles from './BillingNew.module.scss'
import InvoicesColumn from './components/InvoicesColumn'
import PaymentsColumn from './components/PaymentsColumn'
import FindHotelByInvoiceIdDialog from './FindHotelByInvoiceIdDialog'
import { getFilteredInvoices } from './Helpers/getFilteredInvoices'
import { useBillingDialogsManager } from './hooks/useBillingDialogsManager'
import { useInvoiceActions } from './hooks/useInvoiceActions'
import { useInvoiceSentNotification } from './hooks/useInvoiceSentNotification'
import ManagePaymentMethods from './ManagePaymentMethods/index'

const BillingNew = () => {
  const dispatch = useDispatch()

  const billingState = useSelector(s => s.billing)
  const currentHotelName = useSelector(selectCurrentHotelName)
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const isGroupView = useSelector(selectIsInvoiceGroupView)

  const {
    invoices,
    payments,
    isGettingInvoices: isInvoicesLoading,
    isGettingPayments: isPaymentsLoading,
    merchantName,
    totalDue,
    currentBalance: balance,
    activeBalance,
    billingContacts,
    isGettingBillingContacts,
    didInvoiceSentSuccesfully
  } = billingState

  const [searchValue, setSearchValue] = useState('')
  const [showSkippedInvoices, setShowSkippedInvoices] = useState(false)

  const { dialogs, openDialog, closeDialog, setDialogVisibility } =
    useBillingDialogsManager()
  const { isSendingInvoice, handleSendInvoice } = useInvoiceActions()

  useInvoiceSentNotification(didInvoiceSentSuccesfully, setDialogVisibility)

  useEffect(() => {
    dispatch(getInvoicesList())
    dispatch(getPaymentsList())
  }, [dispatch, isGroupView])

  const {
    params: { editBillingInfo: isEditBillingOn }
  } = useRouteMatch({
    path: '/billing/:editBillingInfo?',
    strict: false
  })

  const paymentsInProgress = payments.filter(p => p.processorStatus === 'InitialApproval')

  const paymentAmountInProgress = paymentsInProgress.reduce(
    (prev, curr) => prev + curr.paymentAmount,
    0
  )

  const filteredInvoices = getFilteredInvoices(
    invoices,
    searchValue,
    showSkippedInvoices,
    isInternalAdmin,
    paymentAmountInProgress
  )

  useEffect(() => {
    if (invoices.length > 0) {
      window.invoices = invoices
    }
  }, [invoices])

  function handleShowInvoiceContacts() {
    dispatch(getInvoiceContacts(isGroupView))
    openDialog('billingContacts')
  }

  function handleRetryPayment(id) {
    dispatch(postPaymentRetry(id, isGroupView))
  }

  function handlePaymentUpdate(payment) {
    dispatch(updatePayment(payment, isGroupView))
  }

  if (isInvoicesLoading || isPaymentsLoading) {
    return <LoadingIndicator />
  }

  return (
    <div className={styles.wrapper}>
      {dialogs.findHotelByInvoiceId && (
        <FindHotelByInvoiceIdDialog onClose={() => closeDialog('findHotelByInvoiceId')} />
      )}
      {dialogs.invoiceSent && (
        <ConfirmationDialog
          title="Invoice sent!"
          onConfirm={() => closeDialog('invoiceSent')}
        >
          <p>Invoice sent to selected contacts succesfully!</p>
        </ConfirmationDialog>
      )}
      {dialogs.billingContacts && (
        <BillingContactsDialog
          contacts={billingContacts}
          handleConfirm={handleSendInvoice}
          isSendingInvoice={isSendingInvoice || isGettingBillingContacts}
          handleHideDialog={() => closeDialog('billingContacts')}
        />
      )}
      {dialogs.addPayment && (
        <MarkAsPaid
          title="Add new payment"
          handleClose={() => closeDialog('addPayment')}
        />
      )}
      <DataIsReprocessingBar marginBottom={20} />
      <div className={styles.content}>
        <CustomHeader
          title="Billing and payments"
          subtitle={isGroupView ? merchantName : currentHotelName}
          width={1000}
        >
          {isInternalAdmin && (
            <PrimarySmallButton
              style={{ marginLeft: 12 }}
              onClick={() => openDialog('findHotelByInvoiceId')}
            >
              Find hotel with invoice id
            </PrimarySmallButton>
          )}
        </CustomHeader>
        {isEditBillingOn && <ManagePaymentMethods isGroupView={isGroupView} />}
        <ReactTooltip />
        <InvoicesColumn
          balance={balance}
          activeBalance={activeBalance}
          handleAddPaymentClick={() => openDialog('addPayment')}
          handleShowInvoiceContacts={handleShowInvoiceContacts}
          isInternalAdmin={isInternalAdmin}
          isGroupView={isGroupView}
          invoices={invoices}
          totalDue={totalDue}
          showSkippedInvoices={showSkippedInvoices}
          setShowSkippedInvoices={setShowSkippedInvoices}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          filteredInvoices={filteredInvoices}
        />
        <PaymentsColumn
          styles={styles}
          payments={payments}
          handleRetryPayment={handleRetryPayment}
          handlePaymentUpdate={handlePaymentUpdate}
        />
      </div>
    </div>
  )
}

export default BillingNew
